import { useRef, useState, useMemo, useCallback } from 'react'

import { FormikProps, FormikValues } from 'formik'

import { IOptionProps, useToast } from '@pnld/components-web'
import { useQueryClient } from '@tanstack/react-query'

import {
  useQueryGetFiles,
  useQueryGetProcessingUsers,
} from '@/api/prediction-processing/queries'
import { TGetFilesParams } from '@/api/prediction-processing/types'
import { useQueryGetCheckPredictions } from '@/api/predictions/queries'
import useDebounce from '@/hooks/useDebounce'
import usePaginatedList from '@/hooks/usePaginatedList'

import { formatDate } from './shared'

const CHECK_PREDICTIONS_QUERY_KEY = ['predictions', 'check']

const useSendingsFilesController = ({ onClose }: { onClose: () => void }) => {
  const toast = useToast()
  const queryClient = useQueryClient()
  const formikRef = useRef<FormikProps<FormikValues>>(null)
  const [filters, setFilters] = useState<TGetFilesParams>({
    page: 1,
    pageSize: 10,
  })

  const { data: filesData, isLoading, isFetching } = useQueryGetFiles(filters)
  const { paginationProps, changeFilters } = usePaginatedList(
    filesData?.pagination,
    setFilters
  )

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: useQueryGetFiles.queryKey })
    queryClient.invalidateQueries({ queryKey: CHECK_PREDICTIONS_QUERY_KEY })
  }, [queryClient])

  const { data: processingUsers } = useQueryGetProcessingUsers()
  const userOptions: Array<IOptionProps> = useMemo(() => {
    if (!processingUsers) return []
    return processingUsers.map(user => ({
      label: user.noUserName || user.dsEmail,
      value: user.coUser,
    }))
  }, [processingUsers])

  const tableData = useMemo(() => {
    if (!filesData?.results) return []
    return filesData.results.map(file => ({
      id: file.coProcessing,
      filename: file.dsOriginalFilename,
      username: file.coUser?.noUserName || file.coUser?.dsEmail || '—',
      sendDate: formatDate(file.dtCreatedAt),
      censusYear: file.nuCensusYear,
      predictionYear: file.nuPredictionTargetYear,
      statusCode: file.dsStatus,
      statusDisplay: file.dsStatusDisplay,
      resultMessage: file.dsResultMessage,
    }))
  }, [filesData?.results])

  const debouncedUpdateFilters = useDebounce<Partial<TGetFilesParams>>(
    changeFilters,
    500
  )

  const handleSearchChange = (search: string) => {
    debouncedUpdateFilters({ dsOriginalFilename: search || undefined })
  }

  const handleStartDateChange = (date: string) => {
    debouncedUpdateFilters({ startDate: date || undefined })
  }

  const handleFinalDateChange = (date: string) => {
    debouncedUpdateFilters({ finalDate: date || undefined })
  }

  const handleUserChange = (coUser: number | undefined) => {
    changeFilters({ coUser })
  }

  const handleSubmit = (_values: FormikValues) => {
    // Invalida ambas as queries para refetch imediato após o upload:
    // - 'files': atualiza a tabela com o novo registro de processamento
    // - 'predictions/check': atualiza processingPrediction para ativar o polling
    queryClient.invalidateQueries({ queryKey: useQueryGetFiles.queryKey })
    queryClient.invalidateQueries({ queryKey: CHECK_PREDICTIONS_QUERY_KEY })
    toast({
      title: 'Dados enviados com sucesso',
      description: 'O processamento foi iniciado e pode levar alguns minutos.',
      status: 'success',
      duration: 5000,
      isClosable: true,
    })
    onClose()
  }

  return {
    fileProps: {
      formikRef,
      handleSearchChange,
      handleStartDateChange,
      handleFinalDateChange,
      handleUserChange,
      userOptions,
      handleSubmit,
      handleRefresh,
      tableData,
      isLoading,
      isFetching,
    },
    paginationProps,
  }
}

export const useSendingsController = ({ onClose }: { onClose: () => void }) => {
  // Verificação de processamento ativo ANTES de useSendingsFilesController para
  // que processingPrediction possa controlar o refetchInterval da query de arquivos.
  const { data: checkPredictionsData } = useQueryGetCheckPredictions({
    enabled: true,
  })
  const processingPrediction = !!checkPredictionsData?.processingPrediction

  const { fileProps, paginationProps } = useSendingsFilesController({
    onClose,
  })

  return {
    ...fileProps,
    ...paginationProps,
    processingPrediction,
  }
}
